/**
 * Song default utilities
 *
 * Moved from lib/api.ts for better organization
 */

import type { SongSettings, SectionStructure, AIFeedbackPreferences, AISettings } from '../types/song'
import { DEFAULT_SECTION_SCHEMES } from '../types/rhymeScheme'

// Default AI Feedback Preferences (snake_case to match backend)
export const createDefaultAIFeedbackPreferences = (): AIFeedbackPreferences => ({
  stability_preference: 5,
  genre_context: null,
  custom_genre: null,
  feedback_verbosity: 'medium',
  enabled_features: {
    rhyme: true,
    prosody: true,
    structure: true,
  },
})

// Default AI Settings (nested structure matching backend)
export const createDefaultAISettings = (): AISettings => ({
  creativity_level: 5,
  preserve_user_phrases: true,
  auto_suggestions: true,
  suggestion_frequency: 3,
  writing_style_adaptation: true,
  ai_feedback_preferences: createDefaultAIFeedbackPreferences(),
})

// Helper functions for creating default settings
export const createDefaultSettings = (): SongSettings => ({
  // Core Narrative
  narrative_pov: 'first_person',
  central_theme: undefined,
  foundation_questions: {},
  six_best_friends: {},

  // Structure
  structural_boxes: [],
  section_structure: [],

  // Rhyme and Prosody
  // Note: rhyme_preferences is deprecated as of Scope 21.
  // Use rhyme_schemes for per-section-type configuration instead.
  rhyme_preferences: {},
  rhyme_schemes: DEFAULT_SECTION_SCHEMES,
  prosody_settings: {
    rhythmic_stability: 5,
    phrasing_style: 'balanced',
    syllable_emphasis: true,
    meter_consistency: 5,
  },

  // Content and Style
  keyword_settings: {
    primary_keywords: [],
    metaphor_themes: [],
    avoid_words: [],
    synonym_groups: {},
  },
  style_guide: {
    sub_genres: [],
    artist_references: [],
    avoid_cliches: true,
    innovation_level: 5,
  },

  // Global Targets
  energy_level: 5,

  // AI Assistance (nested structure matching backend)
  ai: createDefaultAISettings(),
})

export const createDefaultSectionStructure = (order: number): SectionStructure => ({
  label: `Section ${order + 1}`,
  type: 'custom',
  order,
})
